import { ProfileDialog } from '@strata/react-dialog';
import { type ColumnDef, Grid, PrimaryData } from '@strata/react-grid';
import { SuccessToast } from '@strata/react-toast-message';

import { useGetUserProfileQuery, usePutUserProfileMutation } from 'store/api/authApi';
import { useGetSegmentsQuery } from 'store/api/referenceApi';

interface Organization {
    organizationName?: string;
    role: string;
}

const columns: ColumnDef<Organization>[] = [
    {
        accessorKey: 'organizationName',
        header: 'Segment Name',
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        accessorKey: 'role',
        header: 'Role',
    },
];

interface UserProfileDialogProps {
    isOpen: boolean;
    onSecondaryButtonClick: () => void;
}

export const UserProfileDialog = ({ isOpen, onSecondaryButtonClick }: UserProfileDialogProps) => {
    const { data: profile, isFetching: isProfileFetching } = useGetUserProfileQuery();
    const [putUserProfile] = usePutUserProfileMutation();
    const { data: segments, isFetching: isSegmentsFetching } = useGetSegmentsQuery();

    const organizations: Organization[] | undefined = profile?.segmentIds?.map((segmentId) => ({
        organizationName: segments?.find((segment) => segment.segmentId === segmentId)?.name,
        role: profile.role,
    }));

    return (
        <ProfileDialog
            isOpen={isOpen}
            isLoading={isProfileFetching || isSegmentsFetching}
            onSecondaryButtonClick={onSecondaryButtonClick}
            onSubmit={async (values) => {
                await putUserProfile(values).unwrap();
                SuccessToast({ message: 'Your profile was updated successfully.' });
                onSecondaryButtonClick();
            }}
            userFullName={profile?.fullName}
            userEmailAddress={profile?.email}
            additionalDialogContent={
                organizations && (
                    <Grid
                        data={organizations}
                        sortProperties={[{ id: 'organizationName', desc: false }]}
                        columns={columns}
                    />
                )
            }
        />
    );
};
